import { AlertCircle, Info } from 'lucide-react';

export interface TenderAnalysisField {
  value: string | number;
  confidence: number;
}

export interface TenderAnalysisBlock {
  text: string;
  found: boolean;
}

export interface TenderAnalysisData {
  seccio1: Record<string, TenderAnalysisField>;
  seccio2: Record<string, TenderAnalysisField>;
  seccio4: Record<string, TenderAnalysisField>;
  seccio8: Record<string, TenderAnalysisField>;
  seccio3_criteris_adjudicacio: TenderAnalysisBlock;
  seccio5_prescripcions_tecniques: TenderAnalysisBlock;
  seccio6_requisits_tecnics: TenderAnalysisBlock;
  seccio7_nota: string;
  documents: { id: string; docType: string; originalFilename: string }[];
}

const SECTION_1_LABELS: Record<string, string> = {
  expedient: 'Expedient',
  entitat_convocant: 'Entitat convocant',
  objecte_contracte: 'Objecte del contracte',
  tipus_contracte: 'Tipus de contracte',
  procediment: 'Procediment',
  codi_cpv: 'Codi CPV',
  divisio_lots: 'Divisió en lots',
  termini_presentacio_ofertes: "Termini de presentació d'ofertes",
  lloc_execucio: "Lloc d'execució",
  perfil_contractant: 'Perfil del contractant',
};

const SECTION_2_LABELS: Record<string, string> = {
  pressupost_base: 'Pressupost base (sense IVA)',
  valor_estimat: 'Valor estimat del contracte (VEC)',
  termini_execucio: "Termini d'execució / lliurament",
  exercici_pressupostari: 'Exercici pressupostari',
  garantia_provisional: 'Garantia provisional',
  garantia_definitiva: 'Garantia definitiva',
  garantia_producte: 'Garantia del producte',
  termini_adjudicacio: "Termini d'adjudicació",
  penalitat_demora: 'Penalitat per demora',
  revisio_preus: 'Revisió de preus',
  forma_pagament: 'Forma de pagament',
  asseguranca_rc: 'Assegurança de responsabilitat civil',
};

const SECTION_4_LABELS: Record<string, string> = {
  solvencia_economica: 'Solvència econòmica (volum anual de negocis)',
  solvencia_tecnica: 'Solvència tècnica (subministraments similars)',
  mitjans_personals: 'Mitjans personals',
};

const SECTION_8_LABELS: Record<string, string> = {
  plataforma: 'Plataforma de licitació',
  sobre_a: 'Sobre A — Documentació administrativa',
  sobre_b: 'Sobre B — Oferta',
  llengua: 'Llengua',
  variants: 'Variants',
  subcontractacio: 'Subcontractació',
  termini_formalitzacio: 'Formalització del contracte',
};

const CURRENCY_FIELDS = ['pressupost_base', 'valor_estimat', 'solvencia_economica', 'solvencia_tecnica'];

function formatValue(field: string, value: string | number): string {
  if (CURRENCY_FIELDS.includes(field) && typeof value === 'number') {
    return value.toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' });
  }
  return String(value);
}

function confidenceBadge(confidence: number) {
  const pct = Math.round(confidence * 100);
  const color = confidence >= 0.85
    ? 'text-green-700 bg-green-100'
    : confidence >= 0.7
      ? 'text-yellow-700 bg-yellow-100'
      : 'text-orange-700 bg-orange-100';
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded ${color}`} title="Confiança de l'extracció">
      {pct}%
    </span>
  );
}

function SectionTable({ title, labels, data }: { title: string; labels: Record<string, string>; data: Record<string, TenderAnalysisField> }) {
  return (
    <div>
      <h3 className="font-semibold mb-2">{title}</h3>
      <div className="border rounded-lg divide-y">
        {Object.entries(labels).map(([field, label]) => {
          const entry = data[field];
          return (
            <div key={field} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
              <span className="text-muted-foreground">{label}</span>
              {entry ? (
                <span className="flex items-center gap-2 text-right font-medium">
                  {formatValue(field, entry.value)}
                  {confidenceBadge(entry.confidence)}
                </span>
              ) : (
                <span className="text-muted-foreground italic">No detectat</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NarrativeBlock({ title, block }: { title: string; block: TenderAnalysisBlock }) {
  return (
    <div>
      <h3 className="font-semibold mb-2">{title}</h3>
      {block.found ? (
        <details className="border rounded-lg">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium bg-muted/50 rounded-lg">
            Text localitzat — revisió manual recomanada
          </summary>
          <pre className="px-3 py-2 text-xs whitespace-pre-wrap max-h-96 overflow-y-auto">{block.text}</pre>
        </details>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground italic border rounded-lg">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          No s'ha localitzat aquesta secció als documents pujats.
        </div>
      )}
    </div>
  );
}

export default function TenderAnalysisReport({ data }: { data: TenderAnalysisData }) {
  return (
    <div className="space-y-6">
      <SectionTable title="1. Identificació del contracte" labels={SECTION_1_LABELS} data={data.seccio1} />
      <SectionTable title="2. Dades econòmiques i terminis clau" labels={SECTION_2_LABELS} data={data.seccio2} />

      <NarrativeBlock title="3. Criteris d'adjudicació" block={data.seccio3_criteris_adjudicacio} />

      <SectionTable title="4. Solvència exigida" labels={SECTION_4_LABELS} data={data.seccio4} />

      <NarrativeBlock title="5. Prescripcions tècniques" block={data.seccio5_prescripcions_tecniques} />
      <NarrativeBlock title="6. Requisits tècnics detallats" block={data.seccio6_requisits_tecnics} />

      <div>
        <h3 className="font-semibold mb-2">7. Valoració estratègica i recomanacions</h3>
        <div className="flex items-start gap-2 px-3 py-2 text-sm border rounded-lg bg-blue-50 text-blue-900">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{data.seccio7_nota}</span>
        </div>
      </div>

      <SectionTable title="8. Com presentar l'oferta" labels={SECTION_8_LABELS} data={data.seccio8} />

      <div className="text-xs text-muted-foreground">
        Informe generat a partir de: {(data.documents ?? []).map(d => `${d.docType} (${d.originalFilename})`).join(', ')}
      </div>
    </div>
  );
}
